/**
 * Web Crypto AES-GCM with PBKDF2-derived key.
 * Data at rest is never plaintext. Passcode never leaves the device.
 */

const PBKDF2_ITERS = 600_000;
const SALT = new TextEncoder().encode('hisaab-v1-salt');

async function deriveKey(passcode: string): Promise<CryptoKey> {
  const passKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passcode),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: SALT, iterations: PBKDF2_ITERS, hash: 'SHA-256' },
    passKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptJSON(passcode: string, data: unknown): Promise<string> {
  const key = await deriveKey(passcode);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(data));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  const merged = new Uint8Array(iv.length + ct.byteLength);
  merged.set(iv, 0);
  merged.set(new Uint8Array(ct), iv.length);
  return btoa(String.fromCharCode(...merged));
}

export async function decryptJSON<T>(passcode: string, blob: string): Promise<T> {
  const key = await deriveKey(passcode);
  const merged = Uint8Array.from(atob(blob), (c) => c.charCodeAt(0));
  const iv = merged.slice(0, 12);
  const ct = merged.slice(12);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return JSON.parse(new TextDecoder().decode(pt)) as T;
}
